import { cva } from 'class-variance-authority'
import type { HTMLAttributes, Key, ReactNode } from 'react'
import { cn } from '../../lib/cn'
import { Button } from '../../atoms/button/button'
import { InfoField } from '../../molecules/info-field/info-field'
import { Title } from '../../molecules/title/title'

/**
 * The Task details card (463:50248, the Figma spells the property "Collpased"):
 * Collpased = False (445:45970) and Collpased = True (463:50186). A white card,
 * 16px radius, 24px padding, drawn 764px wide.
 *
 * Open, a Medium Title is followed 16px below by the description and then the
 * Info fields in rows of three 228px fields, 16px apart (the Figma's `1st row` to
 * `4th row` booleans: rows exist when there are fields for them). The optional
 * Reference material section (its `Reference material` boolean) sits 32px below
 * with its own Medium Title and cards 8px apart in a 708px column, 8px narrower
 * than the card's content (463:54283), and the "View more" text link
 * ends the card 32px below. Collapsed is the Title alone, with the same arrow-up
 * the open card draws (463:50186); the state is the arrow's `aria-expanded`.
 */
export const taskDetailsCardVariants = cva(
	'flex flex-col items-center justify-center gap-8 rounded-atlas-lg bg-atlas-light p-6 font-atlas-sans',
)

/** One read-only detail of the task, e.g. "Brand: NUMELVI". */
export interface TaskDetailsField {
	id: Key
	label: string
	value: ReactNode
	/** A 16px mark on its own row above the value, e.g. the market's flag. */
	flag?: ReactNode
}

export interface TaskDetailsCardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'title'> {
	title?: string
	/** Whether the card is open. */
	isExpanded: boolean
	/** Called with the new state when the Title's arrow is pressed. */
	onExpandedChange?: (isExpanded: boolean) => void
	description?: ReactNode
	descriptionLabel?: string
	/** The details, laid out three to a row. */
	fields?: TaskDetailsField[]
	/** The Reference material cards, e.g. HorizontalCards; omit it to hide the section. */
	referenceMaterial?: ReactNode
	referenceMaterialTitle?: string
	/** Whether the Reference material section is open; defaults to open. */
	isReferenceExpanded?: boolean
	onReferenceExpandedChange?: (isExpanded: boolean) => void
	/** Shows the "View more" text link and is called when it is pressed. */
	onViewMore?: () => void
	viewMoreLabel?: string
}

function chunk<T>(items: T[], size: number): T[][] {
	const rows: T[][] = []
	for (let i = 0; i < items.length; i += size) rows.push(items.slice(i, i + size))
	return rows
}

function TaskBody({
	description,
	descriptionLabel,
	fields,
}: Pick<TaskDetailsCardProps, 'description' | 'descriptionLabel' | 'fields'>) {
	const rows = chunk(fields ?? [], 3)
	return (
		<div className="flex w-full flex-col gap-6">
			{description ? <InfoField className="w-full" label={descriptionLabel ?? ''} value={description} /> : null}
			{rows.length > 0 ? (
				<div className="flex w-full flex-col gap-4">
					{rows.map((row) => (
						<div key={row.map((field) => String(field.id)).join('|')} className="flex w-full items-start gap-4">
							{row.map((field) => (
								<InfoField key={field.id} className="w-57" label={field.label} value={field.value} flag={field.flag} />
							))}
						</div>
					))}
				</div>
			) : null}
		</div>
	)
}

/**
 * A work order's task brief: a collapsible card of read-only details. The open
 * state is controlled by the screen; "View more" and the arrows are callbacks.
 */
export function TaskDetailsCard({
	title = 'Task',
	isExpanded,
	onExpandedChange,
	description,
	descriptionLabel = 'Description',
	fields,
	referenceMaterial,
	referenceMaterialTitle = 'Reference material',
	isReferenceExpanded,
	onReferenceExpandedChange,
	onViewMore,
	viewMoreLabel = 'View more',
	className,
	...props
}: TaskDetailsCardProps) {
	return (
		<div {...props} className={cn(taskDetailsCardVariants(), className)}>
			<div className="flex w-full flex-col gap-8">
				<section className="flex w-full flex-col gap-4">
					<Title size="medium" title={title} isExpanded={isExpanded} onExpandedChange={onExpandedChange} />
					{isExpanded ? (
						<TaskBody description={description} descriptionLabel={descriptionLabel} fields={fields} />
					) : null}
				</section>
				{isExpanded && referenceMaterial ? (
					<section className="flex w-full flex-col gap-4">
						<Title
							size="medium"
							title={referenceMaterialTitle}
							isExpanded={isReferenceExpanded}
							onExpandedChange={onReferenceExpandedChange}
						/>
						{isReferenceExpanded === false ? null : (
							<div className="flex w-177 max-w-full flex-col gap-2">{referenceMaterial}</div>
						)}
					</section>
				) : null}
			</div>
			{isExpanded && onViewMore ? (
				<Button variant="text-link" onPress={onViewMore}>
					{viewMoreLabel}
				</Button>
			) : null}
		</div>
	)
}
